import { computed } from 'vue';
import { useDataStore } from '../../../composables/useVuexStore';
import { toValue } from '../../vueuse/toValue';
import {
  getSelectedPriceData, getPriceRange, getRedesignPriceRange, getOffersData, getWithOffer, getPerksPromotions, getFinalPriceAdditionalOffers,
} from './utils.bcom';
import { PRICE } from '../../../store_modules/traitsStore/types';

function connectToStore() {
  return useDataStore(PRICE);
}

const emptyResponse = {
  selectedPrice: {
    value: null,
  },
};

/**
 * Initializes the data in local "store".
 * This should only need to be called once on a component level.
 * @param {{ id: string, data: TraitPrice }} obj
 */
function useInitPrice({ id, data }) {
  const dataStore = connectToStore();
  if (!dataStore.has(id)) {
    dataStore.set(id, {
      ...data,
    });
  }
}
/**
 *
 * @param {object} param0
 * @param {import('@vueuse/core').MaybeRef<string>} param0.id
 * @param {SelectedColor} param0.selectedColor TODO: remove later and use useTraits
 * @param {{value: number}} param0.selectedUpc  TODO: remove later and use useUpc
 *
 * @returns
 */
function usePrice({
  id, selectedColor, selectedUpc, priceSimplificationEnabled = false,
} = {}) {
  const product = connectToStore().get(toValue(id));
  if (!product || !Object.keys(product).length) return emptyResponse;
  const { isMasterProduct } = product;
  if (isMasterProduct) return emptyResponse;

  const {
    isConsolidatedMaster,
    latchkeyData,
    relationships,
    pricing,
    isColorwayOffers,
    isConsolidatedMasterLatchkeySelect,
    isCCFP,
    ccfp,
    klarnaEligible,
    department,
  } = product;
  const traits = computed(() => (Object.keys(latchkeyData?.selectedLatchkeyData?.traits || {}).length
    ? latchkeyData.selectedLatchkeyData.traits
    : product.traits || {}));
  const upcs = computed(() => relationships?.upcs);
  const colorMap = computed(() => traits.value?.colors?.colorMap);

  const upcPrice = computed(
    () => upcs.value && upcs.value[selectedUpc.value]?.pricing,
  );
  const colorPrice = computed(
    () => colorMap.value && colorMap.value[selectedColor.value?.id]?.pricing,
  );
    // getUpdatedPriceData in backbone
  const pricingData = computed(() => (isConsolidatedMaster && selectedUpc.value
    ? upcPrice.value
    : colorPrice.value));
  const priceData = computed(() => getSelectedPriceData(pricingData.value));
  // To be covered by promotion bcom price story
  // const isCCFP = product.uiKillSwitches.ccfpEnabled && product.ccfp?.ccfpEligible && !product?.detail?.flags?.masterProduct;
  // Dont think this is needed
  // const isPrice = product.flags.masterProduct && !product.flags.isEGiftCard && isCountryEligible && !isGiftProduct && isAvailable,
  // const isKlarna =
  const badgeDataSource = computed(() => {
    if (selectedUpc.value) {
      if (!isConsolidatedMaster) return pricing;
      const selectedId = upcs?.value?.[selectedUpc.value]?.relationships?.selectedProduct || toValue(id);
      return relationships?.memberProductMap?.[selectedId]?.pricing || {};
    }
    if (isConsolidatedMasterLatchkeySelect) {
      return latchkeyData?.selectedLatchkeyData?.pricing || {};
    }
    return pricing;
  });

  const selectedColorOfferIds = computed(
    () => colorPrice.value
      ?.badgeIds || [],
  );
  const finalPrice = computed(() => (isMasterProduct ? {} : colorPrice.value?.price?.finalPrice || {}));

  const offersData = computed(() => getOffersData(badgeDataSource.value, {
    traits: traits.value,
    isUpcSelected: !!selectedUpc.value,
    selectedColorId: selectedColor.value?.id,
    selectedColorName: selectedColor.value?.name,
    isConsolidatedMaster,
    isColorwayOffers,
    priceSimplificationEnabled,
  }));

  const withOfferData = computed(() => getWithOffer({
    offerList: offersData.value?.offerList || [],
    finalPrice: finalPrice.value,
    colorBadges: selectedColorOfferIds.value,
    flags: { isColorwayOffers },
  }));

  const perksPromotions = computed(() => getPerksPromotions({
    offerList: offersData.value?.offerList || [],
    topOffer: offersData.value?.topOffer,
  }));

  const finalPriceAdditionalOffers = computed(() => getFinalPriceAdditionalOffers({
    offerList: offersData.value?.offerList || [],
  }));

  const showOffersAvailableMessage = computed(() => !selectedUpc.value && (!isColorwayOffers || isConsolidatedMaster));
  const showPriceRange = computed(() => isConsolidatedMaster && !selectedUpc.value);
  const newMarkdownConsolidated = computed(() => !!(showPriceRange.value && pricing?.price?.priceType?.text?.length));
  const selectedPrice = computed(() => ({
    ...priceData.value,
    priceType: {
      ...priceData.value.priceType,
      ...(newMarkdownConsolidated.value && { text: pricing.price.priceType.text }),
    },
    priceRange: getPriceRange(
      latchkeyData?.selectedLatchkeyData?.pricing || pricing,
    ),
    priceRangeRedesign: getRedesignPriceRange(
      latchkeyData?.selectedLatchkeyData?.pricing || pricing,
    ),
    // putting it on the same object level as mcom
    priceTypeId: pricingData.value?.price?.priceTypeId,
    offersData: offersData.value,
    perksPromotions: perksPromotions.value,
    finalPriceAdditionalOffers: finalPriceAdditionalOffers.value,
    ccfpData: ccfp || {},
    departmentData: department || {},
    flags: {
      ...priceData.value.flags,
      // showPriceRange: isConsolidatedMaster && !selectedUpc.value,
      showPriceRange: showPriceRange.value,
      newMarkdownConsolidated: newMarkdownConsolidated.value,
      isMasterProduct,
      showOffersAvailableMessage: showOffersAvailableMessage.value,
      // To be covered by promotion bcom price story
      creditFinance: isCCFP || klarnaEligible,
      klarna: klarnaEligible && !showPriceRange.value,
    },
    badgesList: offersData.value.offerList || [],
    id: toValue(id),
    withOfferData: withOfferData.value,
  }));
  return {
    selectedPrice,
  };
}

export default usePrice;

export { usePrice, useInitPrice };
